import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Send } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm text-slate-800 placeholder:text-slate-400 outline-none transition-colors focus:border-brand-orange focus:ring-2 focus:ring-brand-orange/20";

export const QuoteForm = ({ preset, compact = false, onSuccess, source = "quote-form" }) => {
  const [form, setForm] = useState({
    name: "",
    mobile: "",
    email: "",
    location: "",
    property_type: preset?.property_type || "Residential",
    solar_requirement: preset?.solar_requirement || "Not Sure",
    monthly_units: preset?.monthly_units || "",
    message: "",
  });
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.mobile.trim() || !form.location.trim()) {
      toast.error("Please fill your name, mobile number and location.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/enquiry`, { ...form, source });
      toast.success("Thank you! Your enquiry has been received. Our team will contact you shortly.");
      setForm({ name: "", mobile: "", email: "", location: "", property_type: "Residential", solar_requirement: "Not Sure", monthly_units: "", message: "" });
      onSuccess && onSuccess();
    } catch (err) {
      toast.error("Could not submit your enquiry. Please call or WhatsApp us instead.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} className={compact ? "space-y-3" : "grid grid-cols-1 sm:grid-cols-2 gap-4"} data-testid="quote-form">
      <div>
        <input className={inputCls} placeholder="Your name *" value={form.name} onChange={set("name")} data-testid="quote-input-name" />
      </div>
      <div>
        <input className={inputCls} placeholder="Mobile number *" value={form.mobile} onChange={set("mobile")} data-testid="quote-input-mobile" inputMode="tel" />
      </div>
      <div>
        <input className={inputCls} placeholder="Email (optional)" value={form.email} onChange={set("email")} data-testid="quote-input-email" type="email" />
      </div>
      <div>
        <input className={inputCls} placeholder="Location *" value={form.location} onChange={set("location")} data-testid="quote-input-location" />
      </div>
      <div>
        <select className={inputCls} value={form.property_type} onChange={set("property_type")} data-testid="quote-select-property">
          {["Residential", "Commercial", "Industrial", "Agriculture", "Other"].map((o) => (
            <option key={o}>{o}</option>
          ))}
        </select>
      </div>
      <div>
        <select className={inputCls} value={form.solar_requirement} onChange={set("solar_requirement")} data-testid="quote-select-requirement">
          {["Rooftop Solar", "Solar Pump", "On-Grid", "Hybrid", "Off-Grid", "Not Sure"].map((o) => (
            <option key={o}>{o}</option>
          ))}
        </select>
      </div>
      <div>
        <input className={inputCls} placeholder="Avg monthly EB units (optional)" value={form.monthly_units} onChange={set("monthly_units")} data-testid="quote-input-units" inputMode="numeric" />
      </div>
      <div>
        <input className={inputCls} placeholder="Tell us about your requirement" value={form.message} onChange={set("message")} data-testid="quote-input-message" />
      </div>
      <div className={compact ? "" : "sm:col-span-2"}>
        <button
          type="submit"
          disabled={loading}
          className="group flex w-full items-center justify-center gap-2 rounded-full bg-brand-orange px-8 py-3.5 font-heading text-sm font-bold uppercase tracking-wider text-white transition-all hover:bg-brand-orangedark hover:shadow-lg hover:shadow-brand-orange/30 disabled:opacity-60"
          data-testid="quote-submit-btn"
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4 transition-transform group-hover:translate-x-1" />}
          Request Free Quote
        </button>
      </div>
    </form>
  );
};
